import React from 'react';
import { InView } from 'react-intersection-observer';

import IconArrow from '@components/Images/IconArrow';
import LazyImgixImage from '@components/LazyImgixImage';
import TimeAgo from '@components/TimeAgo';
import useEventTracking from '@hooks/useEventTracking';
import { titv_show_path } from '@routes';
import { ClickTarget, ImpressionElement, click, impression } from '@tracking';
import type { TITVEvent } from '@utils/backend-api/titv';
import { getImgixSrc } from '@utils/images';

import type { TITVNetwork } from '../Network/interfaces';
import TITVMultistreamFeatureHeroDescription from './HeroDescription';

interface TITVMultistreamFeatureHeroProps {
  isInProgressStream?: boolean;
  mostRecentStream?: TITVEvent;
  titvNetwork?: TITVNetwork;
}

const TITVMultistreamFeatureHero = ({
  isInProgressStream = false,
  mostRecentStream,
  titvNetwork,
}: TITVMultistreamFeatureHeroProps) => {
  const { trackEvent } = useEventTracking();
  const videoId = mostRecentStream?.videoId;

  const handleLatestEpisodeClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.TITVLatestEvent,
        clickText: mostRecentStream?.title,
        clickLink: titv_show_path({ guid: mostRecentStream?.guid }),
      })
    );
  };

  return (
    <InView
      triggerOnce={true}
      onChange={(inView) => {
        inView &&
          trackEvent(
            impression({
              impressionElement: ImpressionElement.TITVMostRecentStream,
              context: mostRecentStream?.videoId,
            })
          );
      }}
    >
      {!videoId ? (
        <div className="relative mx-auto aspect-[16/9] w-full max-w-[1728px]">
          <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
            <LazyImgixImage
              src={getImgixSrc({
                path: 'titv/TITV_generic_placeholder.png',
                isEnvDependent: false,
              })}
              alt="TITV Placeholder"
              className="max-h-full max-w-full object-cover"
            />
          </div>
        </div>
      ) : mostRecentStream && mostRecentStream?.videoId ? (
        <div className="px-0" data-testid="titv-most-recent-stream">
          <div className="m-auto flex max-w-container-lg flex-col flex-nowrap items-start gap-32 md:gap-56 lg:flex-row">
            <div className="relative -ml-16 aspect-[16/9] w-[calc(100%_+_32px)] flex-shrink-0 sm:-ml-28 sm:w-[calc(100%_+_56px)] sm:max-w-none md:ml-0 md:max-w-full lg:max-w-[748px]">
              <iframe
                allow="autoplay; encrypted-media; fullscreen"
                className="absolute left-0 top-0 h-full w-full border-0"
                src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&mute=0&playsinline=1`}
                title={`Most Recent ${mostRecentStream.title} Stream`}
                allowFullScreen
              />
            </div>
            <div className="flex flex-col-reverse lg:flex-col">
              <TITVMultistreamFeatureHeroDescription
                isInProgressStream={isInProgressStream}
                titvNetwork={titvNetwork}
              />
              <div>
                {mostRecentStream?.guid && (
                  <div className="mb-8 hidden self-start text-right sm:block">
                    <a
                      className="group m-0 flex flex-nowrap items-center justify-start font-suisse-intl-mono text-disclosure-sm text-primary-magenta dark:text-primary-pink"
                      href={titv_show_path({ guid: mostRecentStream?.guid })}
                      onClickCapture={handleLatestEpisodeClick}
                    >
                      <IconArrow className="mb-4 mr-8 hidden h-16 flex-shrink-0 self-end text-primary-magenta transition-transform duration-200 group-hover:translate-x-2 md:inline" />
                      <span>Latest Episode</span>
                    </a>
                  </div>
                )}
                <h3 className="m-0 font-suisse-works text-headline-xxs">
                  {mostRecentStream.title}
                </h3>
                <TimeAgo
                  className="text-grayscale-charcoal"
                  datetime={mostRecentStream.datetime}
                  date={mostRecentStream.datetime}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </InView>
  );
};

export default TITVMultistreamFeatureHero;
